import {
  websocket,
  ConnectionMessageType,
  WebsocketMessage,
  SystemMessageType,
  MetricMessageType,
  MetricSourceType,
  OutboundMessage,
  PeerIdType,
  PeerMessageType,
  AdminMessageType,
  AdminMessage,
} from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import type { PlaycastWebrtcQualityRawStats, PeerQualityRecommendation } from '@playcastdotio/CoreTypes';
import { handlePeerMessage } from './handlers/peer';
import { handleAuthMessage } from './handlers/auth';
import { handleConnectionMessage } from './handlers/connection';
import { handleInvitationMessage } from './handlers/invitation';
import { handleLobbyMessage } from './handlers/lobby';
import { handleOutboundMessage } from './handlers/outbound';
import { getStateStore } from '../../stores';
import { machineIdState } from '../../stores/shared';
import { handleProfileMessage } from './handlers/profile';
import { handleMatchmakerMessage } from './handlers/matchmaker';
import { handleUserEventsMessage } from './handlers/userEvents';
import { handleVpnMessage } from './handlers/vpn';
import { handleHostMessage } from './handlers/host';
import { adminPeerHandler, sessionCoordinator } from '../../../coordinators/init';
import { realtimeMessageRouter } from '../../../services/MessageRouter';
import { startStream } from '../peers/host';
import { USE_SESSION_COORDINATOR } from '../../../config/featureFlags';
import * as Sentry from '@sentry/react';
import { attachSentryTraceToMessage } from './tracing';
import { setHostBroadcastId } from './hostCorrelation';
import { serviceContainer } from '../../../services/ServiceContainer';

const HEARTBEAT_INTERVAL = 10000; // 10 seconds between pings
const RECONNECT_BASE_DELAY = 3000; // initial delay before reconnecting
const RECONNECT_MAX_DELAY = 60000; // cap at 1 minute between retries
const RECONNECT_MAX_ATTEMPTS = 10; // stop retrying after this many consecutive failures
const AUTH_DEADLINE_MS = 15000; // 15 seconds to complete the auth handshake

/**
 * Helper function to gather guest mode context for Sentry diagnostics
 */
const getGuestModeContext = () => {
  const state = getStateStore('ipc');
  return {
    guest_mode_enabled: state.hostMatchmakingState.currentState.guestAccount,
    edition: state.hostState.currentState.edition,
    ad_mode_enabled: state.adModeState.currentState.enabled,
    game_launched: state.playjectorHostModeState.currentState.gameLaunched,
    user_logged_in: state.playjectorHostModeState.currentState.userLoggedIn,
    steam_launched: state.playjectorHostModeState.currentState.steamLaunched,
    host_status: state.hostState.currentState.status,
    matchmakeable: state.hostMatchmakingState.currentState.matchmakeable,
  };
};

let wsConnection: WebSocket | null = null;
let isReady = false;
let messageQueue: WebsocketMessage[] = [];
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let authDeadlineTimeout: NodeJS.Timeout | null = null;
let isReconnecting = false;
let reconnectAttempts = 0;

// Connection event listeners for coordinator architecture
const connectionListeners: Array<() => void> = [];

// After AUTH_SUCCESS (e.g. isCloudHost from server); SessionCoordinator refreshes static props + presence
const hostAuthSuccessListeners: Array<() => void> = [];

// Storage for peer IDs and tokens to support reconnection
interface StoredPeerData {
  peerId: string;
  peerToken: string;
  peerIdType: PeerIdType;
}

const storedPeerData: Record<PeerIdType, StoredPeerData | null> = {
  [PeerIdType.HOST]: null,
  [PeerIdType.PLAYER]: null,
  [PeerIdType.REMOTE_CLIENT]: null,
  [PeerIdType.REMOTE_HOST]: null,
  [PeerIdType.ADMIN]: null,
};

// Helper function to access localStorage safely
const getLocalStorage = (key: string): StoredPeerData | null => {
  // Disable localStorage for now
  return null;
  // try {
  //   const item = localStorage.getItem(key);
  //   return item ? JSON.parse(item) : null;
  // } catch (e) {
  //   console.error(`[Realtime] LocalStorage error for key ${key}:`, e);
  //   return null;
  // }
};

const setLocalStorage = (key: string, value: StoredPeerData): boolean => {
  // Disable localStorage for now
  return true;
  // try {
  //   localStorage.setItem(key, JSON.stringify(value));
  //   return true;
  // } catch (e) {
  //   console.error(`[Realtime] LocalStorage set error for key ${key}:`, e);
  //   return false;
  // }
};

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

const startAuthDeadline = () => {
  clearAuthDeadline();
  authDeadlineTimeout = setTimeout(() => {
    console.error(`[Realtime] Auth handshake did not complete within ${AUTH_DEADLINE_MS}ms — reconnecting`);
    Sentry.addBreadcrumb({
      category: 'websocket',
      message: 'Auth handshake timed out',
      level: 'error',
      data: { deadlineMs: AUTH_DEADLINE_MS, ...getGuestModeContext() },
    });
    Sentry.captureMessage('[Realtime] Auth handshake timed out', { level: 'warning' });
    disconnect();
    handleReconnect();
  }, AUTH_DEADLINE_MS);
};

const clearAuthDeadline = () => {
  if (authDeadlineTimeout) {
    clearTimeout(authDeadlineTimeout);
    authDeadlineTimeout = null;
  }
};

const startHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }

  heartbeatInterval = setInterval(() => {
    if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
      stopHeartbeat();
      return;
    }

    try {
      const pingMessage = websocket.generators.system(SystemMessageType.SYS_PING, {});
      attachSentryTraceToMessage(pingMessage);
      wsConnection.send(JSON.stringify(pingMessage));
        } catch (error) {
          console.error('[Realtime] Failed to send heartbeat:', error);
          Sentry.addBreadcrumb({
            category: 'websocket',
            message: 'Heartbeat send failed',
            level: 'error',
            data: {
              error: error?.toString() || 'Unknown error',
              ...getGuestModeContext(),
            },
          });
          disconnect();
          handleReconnect();
        }
  }, HEARTBEAT_INTERVAL);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

export const handleReconnect = () => {
  if (isReconnecting) {
    console.log('[Realtime] Reconnection already in progress');
    return;
  }

  reconnectAttempts++;

  if (reconnectAttempts > RECONNECT_MAX_ATTEMPTS) {
    console.error(`[Realtime] Max reconnect attempts (${RECONNECT_MAX_ATTEMPTS}) reached — giving up`);
    Sentry.captureMessage('[Realtime] Max reconnect attempts reached, giving up', {
      level: 'fatal',
      tags: { reconnect_exhausted: 'true' },
      extra: { attempts: reconnectAttempts, ...getGuestModeContext() },
    });
    isReconnecting = false;
    return;
  }

  isReconnecting = true;

  const delay = Math.min(RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttempts - 1), RECONNECT_MAX_DELAY);

  console.log(`[Realtime] Initiating reconnection attempt ${reconnectAttempts}/${RECONNECT_MAX_ATTEMPTS} in ${delay}ms`);
  Sentry.addBreadcrumb({
    category: 'websocket',
    message: 'Initiating websocket reconnection',
    level: 'info',
    data: {
      attempt: reconnectAttempts,
      delay,
      ...getGuestModeContext(),
    },
  });

  disconnect();

  clearReconnectTimeout();
  reconnectTimeout = setTimeout(() => {
    try {
      console.log('[Realtime] Attempting to reconnect now');
      wsConnection = null;
      connect();
    } catch (error) {
      console.error('[Realtime] Reconnection attempt failed:', error);
      Sentry.captureException(error);
    } finally {
      isReconnecting = false;
    }
  }, delay);
};

export const connect = () => {
  if (wsConnection?.readyState === WebSocket.OPEN || wsConnection?.readyState === WebSocket.CONNECTING) {
    console.warn('[Realtime] Connection already exists');
    return;
  }

  // Load all peer data from localStorage on connect
  Object.values(PeerIdType).forEach((type) => {
    const storedData = getLocalStorage(`playcast_peer_${type}`);
    if (storedData) {
      storedPeerData[type] = storedData;
      console.log(`[Realtime] Loaded stored peer data for ${type}`, { peerId: storedData.peerId });
    }
  });

  try {
    wsConnection = new WebSocket(constants.REALTIME_API_WEBSOCKET_URL);

    wsConnection.onopen = () => {
      // Always wait for the next tick to ensure the connection is fully established
      setTimeout(() => {
        if (wsConnection?.readyState === WebSocket.OPEN) {
          console.log('[Realtime] Connected, sending init message');
          Sentry.addBreadcrumb({
            category: 'websocket',
            message: 'Realtime websocket connected',
            level: 'info',
            data: {
              url: constants.REALTIME_API_WEBSOCKET_URL,
              version: constants.VERSION,
              ...getGuestModeContext(),
            },
          });
          const msg = websocket.generators.connection(ConnectionMessageType.CONN_INIT, {
            version: constants.VERSION,
          });
          attachSentryTraceToMessage(msg);
          wsConnection?.send(JSON.stringify(msg));
          startHeartbeat();
          startAuthDeadline();
        } else {
          console.warn('[Realtime] Connection not ready after open event');
          Sentry.addBreadcrumb({
            category: 'websocket',
            message: 'Websocket not ready after open event',
            level: 'warning',
            data: getGuestModeContext(),
          });
          handleReconnect();
        }
      }, 0);
    };

    wsConnection.onmessage = (event) => {
      const validation = websocket.validateWebsocketMessage(event.data);
      if (!validation.success) {
        console.error('[Realtime] Invalid message:', validation.error?.message ?? 'unknown');
        return;
      }

      const message = validation.data;
      if (!message) {
        console.error('[Realtime] No message data');
        return;
      }

      try {
        if (websocket.isConnectionMessage(message)) {
          handleConnectionMessage(message, wsConnection).catch((error) => {
            console.error('[Realtime] Connection message handler failed:', error);
            Sentry.captureException(error, {
              tags: { handler: 'handleConnectionMessage' },
            });
            handleReconnect();
          });
        } else if (websocket.isAuthMessage(message)) {
          handleAuthMessage(message);
        } else if (websocket.isInvitationMessage(message)) {
          handleInvitationMessage(message);
        } else if (websocket.isPeerMessage(message)) {
          handlePeerMessage(message);
        } else if (websocket.isLobbyMessage(message)) {
          handleLobbyMessage(message);
        } else if (websocket.isOutboundMessage(message)) {
          handleOutboundMessage(message as OutboundMessage);
        } else if (websocket.isMatchmakingMessage(message)) {
          handleMatchmakerMessage(message);
        } else if (websocket.isProfileMessage(message)) {
          handleProfileMessage(message);
        } else if (websocket.isUserEventMessage(message)) {
          handleUserEventsMessage(message);
        } else if (websocket.isVpnMessage(message)) {
          handleVpnMessage(message);
        } else if (websocket.isHostMessage(message)) {
          handleHostMessage(message);
        } else if (websocket.isBeaconMessage(message)) {
          realtimeMessageRouter.route(message);
        } else if (websocket.isProvisioningMessage(message)) {
          realtimeMessageRouter.route(message);
        } else if (websocket.isMacroMessage(message)) {
          console.log('[StatefulMacroSystemStep] 🛬 Routing inbound macro message from realtime-api', {
            type: message.type,
          });
          realtimeMessageRouter.route(message);
        } else if (websocket.isAdminMessage(message)) {
          const adminMsg = message as AdminMessage;
          if (adminMsg.type === AdminMessageType.ADMIN_SESSION_PREPARE) {
            console.log('[Realtime] Received ADMIN_SESSION_PREPARE, starting broadcast peer');
            // In coordinator mode the broadcast peer is owned by the SessionCoordinator /
            // PeerCoordinator (which also serves the admin ShadowCam stream). Admin-exit
            // teardowns (restart_game, keep_game) flow through SessionCoordinator.stopStream,
            // so the broadcast START must use the same subsystem — otherwise the legacy
            // startStream() leaves stale hostState='streaming' that makes the next prepare
            // bail with "Already streaming!", causing the realtime-api prepare to time out
            // and deny the reconnect with `machine_offline`.
            if (USE_SESSION_COORDINATOR) {
              void sessionCoordinator.startStream().catch((err) => {
                console.error('[Realtime] Failed to start admin broadcast peer via SessionCoordinator:', err);
              });
            } else {
              startStream();
            }
          } else if (adminPeerHandler) {
            adminPeerHandler.handleAdminSessionMessage(adminMsg);
          }
        } else {
          console.warn('[Realtime] Unhandled inbound websocket message type:', message.type);
        }
      } catch (e) {
        console.error('[Realtime] Failed to handle message:', {
          error: e instanceof Error ? e.message : String(e),
          stack: e instanceof Error ? e.stack : undefined,
        });
      }
    };

    wsConnection.onclose = () => {
      console.log('[Realtime] WebSocket closed');
      Sentry.addBreadcrumb({
        category: 'websocket',
        message: 'Realtime websocket closed',
        level: 'warning',
        data: {
          willReconnect: !isReconnecting,
          ...getGuestModeContext(),
        },
      });
      isReady = false;
      stopHeartbeat();

      serviceContainer.analyticsManager.sendNetworkDisconnectedEvent(machineIdState.currentState);

      // Only attempt reconnect if we weren't already trying to disconnect
      if (!isReconnecting) {
        handleReconnect();
      }
    };

    wsConnection.onerror = (error) => {
      console.error('[Realtime] WebSocket error:', error);
      Sentry.addBreadcrumb({
        category: 'websocket',
        message: 'Realtime websocket error',
        level: 'error',
        data: {
          error: error?.toString() || 'Unknown error',
          ...getGuestModeContext(),
        },
      });
      // Don't call handleReconnect here - let onclose handle it
    };
  } catch (error) {
    console.error('[Realtime] Failed to connect:', error);
    handleReconnect();
  }
};

const processQueue = () => {
  while (messageQueue.length > 0 && isReady && wsConnection?.readyState === WebSocket.OPEN) {
    const message = messageQueue.shift();
    if (message) {
      try {
        attachSentryTraceToMessage(message);
        wsConnection.send(JSON.stringify(message));
      } catch (error) {
        console.error('[Realtime] Failed to send queued message:', error);
        messageQueue.unshift(message); // Put the message back at the start of the queue
        break;
      }
    }
  }
};

export const disconnect = () => {
  stopHeartbeat();
  clearReconnectTimeout();
  clearAuthDeadline();
  isReady = false;
  messageQueue = [];

  if (wsConnection) {
    // Store reference and null the connection before closing
    const conn = wsConnection;
    wsConnection = null;

    try {
      conn.close();
    } catch (error) {
      console.error('[Realtime] Error while closing connection:', error);
    }
  }
};

export const setIsReady = (ready: boolean) => {
  isReady = ready;
  if (isReady) {
    const isReconnect = reconnectAttempts > 0;
    reconnectAttempts = 0;
    clearAuthDeadline();
    processQueue();

    serviceContainer.analyticsManager.sendNetworkConnectedEvent(isReconnect);

    // Notify connection listeners (for coordinator architecture)
    connectionListeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('[Realtime] Connection listener error:', error);
      }
    });
  }
};

/**
 * Register a listener for connection events (for coordinator architecture)
 * @returns Unsubscribe function
 */
export const onConnected = (listener: () => void): (() => void) => {
  connectionListeners.push(listener);

  // Return unsubscribe function
  return () => {
    const index = connectionListeners.indexOf(listener);
    if (index > -1) {
      connectionListeners.splice(index, 1);
    }
  };
};

/**
 * Register a listener for host AUTH_SUCCESS (coordinator path; avoids importing coordinators/init from auth).
 * @returns Unsubscribe function
 */
export const onHostAuthSuccess = (listener: () => void): (() => void) => {
  hostAuthSuccessListeners.push(listener);
  return () => {
    const index = hostAuthSuccessListeners.indexOf(listener);
    if (index > -1) {
      hostAuthSuccessListeners.splice(index, 1);
    }
  };
};

export const notifyHostAuthSuccess = (): void => {
  hostAuthSuccessListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('[Realtime] Host auth success listener error:', error);
    }
  });
};

export const sendMessage = (message: WebsocketMessage) => {
  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN || !isReady) {
    console.warn('[Realtime] Connection not ready, queueing message');
    messageQueue.push(message);
    return;
  }

  // Automatically attach Sentry trace context to ALL outgoing messages
  // This enables distributed tracing across the full connection lifecycle
  attachSentryTraceToMessage(message);

  try {
    wsConnection.send(JSON.stringify(message));
  } catch (error) {
    console.error('[Realtime] Failed to send message:', error);
    messageQueue.push(message);
  }
};

// Modified to store peer IDs and tokens with localStorage persistence
export const requestPeerId = (peerIdType: PeerIdType) => {
  // Check memory first
  let storedData = storedPeerData[peerIdType];

  // If not in memory, try localStorage
  if (!storedData) {
    const storedJson = getLocalStorage(`playcast_peer_${peerIdType}`);
    if (storedJson) {
      storedData = storedJson;
      // Also update the in-memory cache
      storedPeerData[peerIdType] = storedData;
      console.log(`[Realtime] Loaded peer data from localStorage for ${peerIdType}`, { peerId: storedData?.peerId });
    }
  }

  if (storedData) {
    console.log(`[Realtime] Attempting to reclaim peer ID for ${peerIdType}`, storedData.peerId);
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
        peerIdType,
        peerId: storedData.peerId,
        peerToken: storedData.peerToken,
      }),
    );
  } else {
    // Request a new peer ID if none is stored
    console.log(`[Realtime] Requesting new peer ID for ${peerIdType}`);
    sendMessage(websocket.generators.peer(PeerMessageType.PEER_GET_ID, { peerIdType }));
  }
};

// Export a function to store peer IDs and tokens when received
export const storePeerData = (peerIdType: PeerIdType, peerId: string, peerToken?: string) => {
  if (peerToken) {
    // Store in memory
    const peerData = { peerId, peerToken, peerIdType };
    storedPeerData[peerIdType] = peerData;

    // The host's HOST peer id IS its broadcast id; expose it so outbound
    // messages stamp _broadcastId once broadcasting begins.
    if (peerIdType === PeerIdType.HOST) {
      setHostBroadcastId(peerId);
    }

    // Also store in localStorage for persistence across refreshes
    setLocalStorage(`playcast_peer_${peerIdType}`, peerData);

    console.log(`[Realtime] Stored peer data for ${peerIdType}`, { peerId, tokenPreview: peerToken.substring(0, 5) + '...' });
  }
};

// Export a function to clear peer data for a specific peer ID type
export const clearPeerData = (peerIdType: PeerIdType) => {
  // Clear from memory
  storedPeerData[peerIdType] = null;

  if (peerIdType === PeerIdType.HOST) {
    setHostBroadcastId(undefined);
  }

  // Clear from localStorage if we're using it
  try {
    localStorage.removeItem(`playcast_peer_${peerIdType}`);
  } catch (e) {
    // Ignore localStorage errors
  }

  console.log(`[Realtime] Cleared peer data for ${peerIdType}`);
};

// Host-side WebRTC stats type
export type WebrtcQualityStatsMetric = {
  recommendation: PeerQualityRecommendation;
  reason: string;
  rawStats: PlaycastWebrtcQualityRawStats;
};

// Host-side function to send WebRTC stats for a specific connected peer
export const sendHostMetricBatchWebrtcStats = (
  stats: WebrtcQualityStatsMetric[],
  collectionInterval: number,
  peerMetadata: {
    playerConnectionId: string;
    persistentId: string;
    playerAuthId: string;
    broadcastSessionId: string;
    inviteId: string;
  }
) => {
  console.log('[Host] Sending webrtc quality stats for peer:', peerMetadata.playerConnectionId);
  const message = websocket.generators.metrics(MetricMessageType.METRICS_WEBRTC_QUALITY, {
    payload: {
      name: 'webrtc_quality',
      timestamp: new Date().toISOString(),
      payload: {
        sourceType: MetricSourceType.HOST, // HOST perspective
        collectionInterval,
        broadcastSessionId: peerMetadata.broadcastSessionId,
        environment: constants.ENVIRONMENT,
        inviteId: peerMetadata.inviteId,
        persistentId: peerMetadata.persistentId,
        playerAuthId: peerMetadata.playerAuthId,
        playerConnectionId: peerMetadata.playerConnectionId,
        version: constants.VERSION,
        qualityStats: stats,
      },
    },
  });
  sendMessage(message);
};

// const forceWebsocketCrash = () => {
//   // This is to mimic the server crashing.
//   if (wsConnection) {
//     wsConnection.close(1000, 'Test crash');
//   }
// };

// // Add keybind for crashing
// document.addEventListener('keydown', (event) => {
//   if (event.key === 'F10') {
//     alert('Crashing websocket');
//     forceWebsocketCrash();
//   }
// });
